import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ClinicLayout from '../components/ClinicLayout';
import AnalyticsChart from '../components/AnalyticsChart';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const ClinicDashboard = () => {
  const { user, token, loading: authLoading, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // State for clinic data
  const [clinic, setClinic] = useState(null);
  const [stats, setStats] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // State for clinic creation form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    bookingMode: 'patient_selects'
  });
  const [creating, setCreating] = useState(false);

  // State for adding doctor
  const [showAddDoctor, setShowAddDoctor] = useState(false);
  const [doctorForm, setDoctorForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    bio: '',
    specialties: ''
  });
  const [addingDoctor, setAddingDoctor] = useState(false);

  // State for settings update
  const [updating, setUpdating] = useState(false);

  // Date range filter state for link-in-bio analytics
  const [analyticsDateRange, setAnalyticsDateRange] = useState('30'); // 7, 30, 90, or 'custom'
  const [analyticsDateFrom, setAnalyticsDateFrom] = useState('');
  const [analyticsDateTo, setAnalyticsDateTo] = useState('');

  // Analytics time-series data
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  useEffect(() => {
    // Redirect if not authenticated or not a clinic owner
    if (!authLoading && (!isAuthenticated || user?.role !== 'clinic_owner')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch clinic data
  useEffect(() => {
    const fetchClinicData = async () => {
      if (!token || !isAuthenticated || user?.role !== 'clinic_owner') return;

      try {
        setLoading(true);

        // First try to get existing clinic
        const clinicResponse = await fetch(`${API_URL}/clinics/my/clinic`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (clinicResponse.ok) {
          const clinicData = await clinicResponse.json();
          setClinic(clinicData);

          // Build query params for date range filtering
          const params = new URLSearchParams();
          if (analyticsDateRange === '7days') {
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 7);
            params.set('dateFrom', startDate.toISOString().split('T')[0]);
            params.set('dateTo', endDate.toISOString().split('T')[0]);
          } else if (analyticsDateRange === '30days') {
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 30);
            params.set('dateFrom', startDate.toISOString().split('T')[0]);
            params.set('dateTo', endDate.toISOString().split('T')[0]);
          } else if (analyticsDateRange === 'custom' && analyticsDateFrom && analyticsDateTo) {
            params.set('dateFrom', analyticsDateFrom);
            params.set('dateTo', analyticsDateTo);
          }

          const statsUrl = params.toString()
            ? `${API_URL}/clinics/${clinicData.id}/stats?${params.toString()}`
            : `${API_URL}/clinics/${clinicData.id}/stats`;

          // Fetch clinic stats
          const statsResponse = await fetch(statsUrl, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            setStats(statsData);
          }

          // Fetch clinic doctors
          const doctorsResponse = await fetch(`${API_URL}/clinics/${clinicData.id}/doctors`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (doctorsResponse.ok) {
            const doctorsData = await doctorsResponse.json();
            setDoctors(doctorsData.doctors || []);
          }
        } else if (clinicResponse.status === 404) {
          // No clinic yet, show creation form
          setShowCreateForm(true);
        } else {
          const errorData = await clinicResponse.json();
          setError(errorData.error || 'Failed to fetch clinic data');
        }
      } catch (err) {
        console.error('Error fetching clinic data:', err);
        setError('Failed to connect to server');
      } finally {
        setLoading(false);
      }
    };

    fetchClinicData();
  }, [token, isAuthenticated, user, analyticsDateRange, analyticsDateFrom, analyticsDateTo]);

  // Fetch analytics data when clinic is loaded
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!clinic?.id || !token) return;

      setIsLoadingAnalytics(true);

      try {
        // Build query parameters
        let queryParams = '';
        if (analyticsDateRange === 'custom' && analyticsDateFrom && analyticsDateTo) {
          queryParams = `?from=${analyticsDateFrom}&to=${analyticsDateTo}`;
        } else {
          queryParams = `?days=${analyticsDateRange}`;
        }

        const response = await fetch(`${API_URL}/clinics/${clinic.id}/analytics${queryParams}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAnalyticsData(data);
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
        // Fail silently
      } finally {
        setIsLoadingAnalytics(false);
      }
    };

    fetchAnalyticsData();
  }, [clinic?.id, token, analyticsDateRange, analyticsDateFrom, analyticsDateTo]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCreateClinic = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/clinics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(createForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create clinic');
      }

      setClinic(data.clinic);
      setShowCreateForm(false);
      setStats({
        doctors: 0,
        appointmentsThisMonth: { total: 0, completed: 0, cancelled: 0, upcoming: 0 },
        revenueThisMonth: 0,
        reviews: { count: 0, averageRating: '0.0' }
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleAddDoctor = async (e) => {
    e.preventDefault();
    setAddingDoctor(true);
    setError('');

    try {
      const specialtiesArray = doctorForm.specialties
        .split(',')
        .map(s => s.trim())
        .filter(s => s);

      const response = await fetch(`${API_URL}/clinics/${clinic.id}/doctors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...doctorForm,
          specialties: specialtiesArray
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.errors?.[0]?.msg || 'Failed to add doctor');
      }

      setDoctors(prev => [...prev, data.doctor]);
      setShowAddDoctor(false);
      setDoctorForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        bio: '',
        specialties: ''
      });

      // Update stats
      if (stats) {
        setStats(prev => ({ ...prev, doctors: prev.doctors + 1 }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingDoctor(false);
    }
  };

  const handleUpdateBookingMode = async (newMode) => {
    setUpdating(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/clinics/${clinic.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ bookingMode: newMode })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update booking mode');
      }

      setClinic(prev => ({ ...prev, bookingMode: newMode }));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveDoctor = async (doctorId) => {
    if (!confirm('Are you sure you want to remove this doctor from the clinic?')) return;

    try {
      const response = await fetch(`${API_URL}/clinics/${clinic.id}/doctors/${doctorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove doctor');
      }

      setDoctors(prev => prev.filter(d => d.id !== doctorId));
      if (stats) {
        setStats(prev => ({ ...prev, doctors: Math.max(0, prev.doctors - 1) }));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'clinic_owner') {
    return null;
  }

  // Clinic Creation Form
  if (showCreateForm && !clinic) {
    return (
      <ClinicLayout>
        <div className="px-4 py-8">
          <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
              Create Your Clinic
            </h1>
            <p className="font-body text-light-text-secondary dark:text-silver/60">
              Set up your clinic to start managing doctors and appointments
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="glass border border-light-border dark:border-border rounded-2xl p-8">
            <form onSubmit={handleCreateClinic} className="space-y-6">
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                  Clinic Name *
                </label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                  className="input-field"
                  placeholder="Medical Center London"
                  required
                  disabled={creating}
                />
              </div>

              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                  Description
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  className="input-field min-h-[120px] resize-none"
                  placeholder="Describe your clinic and services..."
                  disabled={creating}
                />
              </div>

              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                  Booking Mode
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label
                    className={`flex flex-col p-4 rounded-xl border cursor-pointer transition-smooth ${
                      createForm.bookingMode === 'patient_selects'
                        ? 'border-light-text dark:border-pure-white bg-white/5'
                        : 'border-light-border dark:border-border hover:border-slate-300 dark:hover:border-white/20'
                    }`}
                  >
                    <input
                      type="radio"
                      name="bookingMode"
                      value="patient_selects"
                      checked={createForm.bookingMode === 'patient_selects'}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, bookingMode: e.target.value }))}
                      className="sr-only"
                    />
                    <span className="font-mono text-sm uppercase tracking-wider mb-1">
                      Patient Selects
                    </span>
                    <span className="font-body text-xs text-light-text-secondary dark:text-silver/60">
                      Patients choose their preferred doctor
                    </span>
                  </label>
                  <label
                    className={`flex flex-col p-4 rounded-xl border cursor-pointer transition-smooth ${
                      createForm.bookingMode === 'round_robin'
                        ? 'border-light-text dark:border-pure-white bg-white/5'
                        : 'border-light-border dark:border-border hover:border-slate-300 dark:hover:border-white/20'
                    }`}
                  >
                    <input
                      type="radio"
                      name="bookingMode"
                      value="round_robin"
                      checked={createForm.bookingMode === 'round_robin'}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, bookingMode: e.target.value }))}
                      className="sr-only"
                    />
                    <span className="font-mono text-sm uppercase tracking-wider mb-1">
                      Round Robin
                    </span>
                    <span className="font-body text-xs text-light-text-secondary dark:text-silver/60">
                      Auto-assign to next available doctor
                    </span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={creating}
              >
                {creating ? 'Creating Clinic...' : 'Create Clinic'}
              </button>
            </form>
          </div>
        </div>
        </div>
      </ClinicLayout>
    );
  }

  // Main Dashboard View
  return (
    <ClinicLayout>
      <div className="px-4 py-8">
        <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
              {clinic?.name || 'Clinic Dashboard'}
            </h1>
            <p className="font-body text-light-text-secondary dark:text-silver/60">
              Welcome back, {user?.firstName} {user?.lastName}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-slate-300 dark:hover:border-white/20 transition-smooth"
          >
            Logout
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
            {error}
            <button
              onClick={() => setError('')}
              className="ml-4 underline hover:no-underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
              Doctors
            </p>
            <p className="font-display text-3xl italic">{stats?.doctors || 0}</p>
          </div>
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
              Appointments This Month
            </p>
            <p className="font-display text-3xl italic">{stats?.appointmentsThisMonth?.total || 0}</p>
          </div>
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
              Revenue This Month
            </p>
            <p className="font-display text-3xl italic">
              {stats?.revenueThisMonth ? `£${stats.revenueThisMonth.toFixed(2)}` : '£0.00'}
            </p>
          </div>
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
              Average Rating
            </p>
            <p className="font-display text-3xl italic">
              {stats?.reviews?.averageRating && stats.reviews.averageRating !== '0.0'
                ? `${stats.reviews.averageRating} ★`
                : '-'}
            </p>
          </div>
        </div>

        {/* Link-in-Bio Analytics */}
        {clinic?.slug && stats?.linkInBioAnalytics && (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                Link-in-Bio Analytics
              </h2>

              {/* Date Range Filter */}
              <div className="flex items-center gap-3">
                <select
                  value={analyticsDateRange}
                  onChange={(e) => setAnalyticsDateRange(e.target.value)}
                  className="px-3 py-2 bg-white dark:bg-charcoal border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-light-text/20 dark:focus:ring-silver/20"
                >
                  <option value="7">Last 7 Days</option>
                  <option value="30">Last 30 Days</option>
                  <option value="90">Last 90 Days</option>
                  <option value="custom">Custom Range</option>
                </select>

                {analyticsDateRange === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={analyticsDateFrom}
                      onChange={(e) => setAnalyticsDateFrom(e.target.value)}
                      className="px-3 py-2 bg-white dark:bg-charcoal border border-light-border dark:border-border rounded-xl font-mono text-xs focus:outline-none focus:ring-2 focus:ring-light-text/20 dark:focus:ring-silver/20"
                    />
                    <span className="text-light-text-secondary dark:text-silver/60">to</span>
                    <input
                      type="date"
                      value={analyticsDateTo}
                      onChange={(e) => setAnalyticsDateTo(e.target.value)}
                      className="px-3 py-2 bg-white dark:bg-charcoal border border-light-border dark:border-border rounded-xl font-mono text-xs focus:outline-none focus:ring-2 focus:ring-light-text/20 dark:focus:ring-silver/20"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                  Total Page Views
                </p>
                <p className="font-display text-3xl italic">
                  {stats.linkInBioAnalytics.pageViews.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                  Unique Visitors
                </p>
                <p className="font-display text-3xl italic">
                  {(stats.linkInBioAnalytics.uniqueVisitors || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                  Bookings from Link-in-Bio
                </p>
                <p className="font-display text-3xl italic">
                  {stats.linkInBioAnalytics.bookingsFromLinkInBio}
                </p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-2">
                  Conversion Rate
                </p>
                <p className="font-display text-3xl italic">
                  {stats.linkInBioAnalytics.conversionRate}%
                </p>
              </div>
            </div>

            {/* Time-Series Chart */}
            <div>
              <h3 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
                Trend Over Time
              </h3>
              {isLoadingAnalytics ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-pulse text-slate-400 dark:text-silver/40">
                    Loading chart...
                  </div>
                </div>
              ) : analyticsData?.timeSeries ? (
                <AnalyticsChart data={analyticsData.timeSeries} />
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400 dark:text-silver/40">
                  No analytics data available
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Doctors List */}
          <div className="lg:col-span-2 glass border border-light-border dark:border-border rounded-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                Clinic Doctors
              </h2>
              <button
                onClick={() => setShowAddDoctor(true)}
                className="px-4 py-2 bg-gradient-silver text-obsidian rounded-xl font-mono text-xs uppercase tracking-widest hover:shadow-glow transition-smooth"
              >
                Add Doctor
              </button>
            </div>

            {doctors.length === 0 ? (
              <div className="text-center py-12">
                <p className="font-body text-light-text-secondary dark:text-silver/60 mb-4">
                  No doctors in your clinic yet
                </p>
                <p className="font-body text-sm text-slate-400 dark:text-silver/40">
                  Add doctors to start accepting appointments
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {doctors.map((doctor) => (
                  <div
                    key={doctor.id}
                    className="flex items-center justify-between p-4 border border-light-border dark:border-border rounded-xl hover:border-slate-300 dark:hover:border-white/20 transition-smooth"
                  >
                    <div>
                      <p className="font-body font-medium">
                        Dr. {doctor.firstName} {doctor.lastName}
                      </p>
                      <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60">{doctor.email}</p>
                      {doctor.specialties && doctor.specialties.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {(typeof doctor.specialties === 'string'
                            ? JSON.parse(doctor.specialties)
                            : doctor.specialties
                          ).slice(0, 3).map((spec, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-slate-100 dark:bg-white/5 border border-light-border dark:border-border rounded text-xs font-mono"
                            >
                              {spec}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveDoctor(doctor.id)}
                      className="px-3 py-1 text-red-400 border border-red-400/30 rounded-lg font-mono text-xs uppercase tracking-wider hover:bg-red-500/10 transition-smooth"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Settings Panel */}
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
              Clinic Settings
            </h2>

            {/* Booking Mode Setting */}
            <div className="mb-6">
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-3">
                Booking Mode
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => handleUpdateBookingMode('patient_selects')}
                  disabled={updating}
                  className={`w-full p-4 border rounded-xl text-left transition-smooth ${
                    clinic?.bookingMode === 'patient_selects'
                      ? 'border-light-text dark:border-pure-white bg-white/5'
                      : 'border-light-border dark:border-border hover:border-slate-300 dark:hover:border-white/20'
                  } ${updating ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <p className="font-mono text-sm uppercase tracking-wider">
                    Patient Selects
                  </p>
                  <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                    Patients choose their doctor
                  </p>
                </button>
                <button
                  onClick={() => handleUpdateBookingMode('round_robin')}
                  disabled={updating}
                  className={`w-full p-4 border rounded-xl text-left transition-smooth ${
                    clinic?.bookingMode === 'round_robin'
                      ? 'border-light-text dark:border-pure-white bg-white/5'
                      : 'border-light-border dark:border-border hover:border-slate-300 dark:hover:border-white/20'
                  } ${updating ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <p className="font-mono text-sm uppercase tracking-wider">
                    Round Robin
                  </p>
                  <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                    Auto-assign to next available
                  </p>
                </button>
              </div>
            </div>

            {/* Link-in-Bio URL */}
            {clinic?.slug && (
              <div className="mb-6 p-4 border border-light-border dark:border-border rounded-xl">
                <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-3">
                  Link-in-Bio URL
                </p>
                <a
                  href={`${window.location.origin}/${clinic.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-sm text-light-text dark:text-pure-white hover:text-light-text-secondary dark:hover:text-silver/80 transition-smooth inline-flex items-center gap-2 break-all"
                >
                  {window.location.origin}/{clinic.slug}
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/${clinic.slug}`;
                    navigator.clipboard.writeText(url);
                    // Show copied feedback (simple approach)
                    const btn = document.activeElement;
                    const originalText = btn.textContent;
                    btn.textContent = 'Copied!';
                    setTimeout(() => {
                      btn.textContent = originalText;
                    }, 2000);
                  }}
                  className="mt-3 w-full px-4 py-2 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-wider hover:border-slate-300 dark:hover:border-white/20 transition-smooth"
                >
                  Copy URL
                </button>
              </div>
            )}

            {/* Link-in-Bio Link */}
            <div className="mb-6">
              <Link
                to="/clinic/link-in-bio"
                className="block w-full p-4 border border-light-border dark:border-border rounded-xl text-center hover:border-slate-300 dark:hover:border-white/20 transition-smooth"
              >
                <p className="font-mono text-sm uppercase tracking-wider">
                  Link-in-Bio Settings
                </p>
                <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                  Set custom URL slug
                </p>
              </Link>
            </div>

            {/* Clinic Info */}
            <div className="pt-4 border-t border-light-border dark:border-border">
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-3">
                Clinic Details
              </p>
              <div className="space-y-3">
                <div>
                  <p className="font-mono text-xs text-slate-400 dark:text-silver/40">Name</p>
                  <p className="font-body text-sm">{clinic?.name}</p>
                </div>
                {clinic?.description && (
                  <div>
                    <p className="font-mono text-xs text-slate-400 dark:text-silver/40">Description</p>
                    <p className="font-body text-sm text-slate-500 dark:text-silver/80">{clinic?.description}</p>
                  </div>
                )}
                <div>
                  <p className="font-mono text-xs text-slate-400 dark:text-silver/40">Clinic ID</p>
                  <p className="font-mono text-xs">{clinic?.id}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="mt-8 glass border border-light-border dark:border-border rounded-2xl p-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Account Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                Email
              </p>
              <p className="font-body">{user?.email}</p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                Role
              </p>
              <p className="font-body capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                Name
              </p>
              <p className="font-body">{user?.firstName} {user?.lastName}</p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                User ID
              </p>
              <p className="font-body font-mono text-xs">{user?.id}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Doctor Modal */}
      {showAddDoctor && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="glass border border-light-border dark:border-border rounded-2xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl italic">Add Doctor</h2>
              <button
                onClick={() => setShowAddDoctor(false)}
                className="text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth"
              >
                ✕
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleAddDoctor} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={doctorForm.firstName}
                    onChange={(e) => setDoctorForm(prev => ({ ...prev, firstName: e.target.value }))}
                    className="input-field"
                    required
                    disabled={addingDoctor}
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={doctorForm.lastName}
                    onChange={(e) => setDoctorForm(prev => ({ ...prev, lastName: e.target.value }))}
                    className="input-field"
                    required
                    disabled={addingDoctor}
                  />
                </div>
              </div>

              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={doctorForm.email}
                  onChange={(e) => setDoctorForm(prev => ({ ...prev, email: e.target.value }))}
                  className="input-field"
                  required
                  disabled={addingDoctor}
                />
              </div>

              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                  Password * (min 8 characters)
                </label>
                <input
                  type="password"
                  value={doctorForm.password}
                  onChange={(e) => setDoctorForm(prev => ({ ...prev, password: e.target.value }))}
                  className="input-field"
                  minLength={8}
                  required
                  disabled={addingDoctor}
                />
              </div>

              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                  Bio
                </label>
                <textarea
                  value={doctorForm.bio}
                  onChange={(e) => setDoctorForm(prev => ({ ...prev, bio: e.target.value }))}
                  className="input-field min-h-[80px] resize-none"
                  placeholder="Brief description of the doctor..."
                  disabled={addingDoctor}
                />
              </div>

              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                  Specialties (comma-separated)
                </label>
                <input
                  type="text"
                  value={doctorForm.specialties}
                  onChange={(e) => setDoctorForm(prev => ({ ...prev, specialties: e.target.value }))}
                  className="input-field"
                  placeholder="General Practice, Pediatrics"
                  disabled={addingDoctor}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddDoctor(false)}
                  className="flex-1 px-4 py-3 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-slate-300 dark:hover:border-white/20 transition-smooth"
                  disabled={addingDoctor}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={addingDoctor}
                >
                  {addingDoctor ? 'Adding...' : 'Add Doctor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </ClinicLayout>
  );
};

export default ClinicDashboard;
